import { NotePurposeTypesEnum, NoteStatusTypes, NoteTypes } from '../../../../utilities/Constants';
import { Placeholder, Stack } from 'react-bootstrap';
import React, { useEffect, useRef, useState } from 'react';

import { INoteModel } from '../../../../interfaces/INote';
import { LoadingButton } from '../../../../shared/buttons/LoadingButton';
import { cloneDeep } from 'lodash';
import { showErrorToast } from '../../../../shared/ToastUtils';
import { useGetNotes } from './useGetNotes';
import { useQueryClient } from 'react-query';
import { useZoneContext } from '../../../../contexts/useZoneContext';

export function UserTaskList() {
    const zoneContext = useZoneContext();
    const queryClient = useQueryClient();
    const notesApi = useGetNotes(NoteTypes.Personal);
    const notes = notesApi.data ?? [];

    const newNoteInputRef = useRef<HTMLInputElement>(null);

    const [pendingResponse, setPendingResponse] = useState(false);
    const [pendingDeleteGuids, setPendingDeleteGuids] = useState<string[]>([]);
    const [showInput, setShowInput] = useState(false);

    const updateCachedNotes = (updatedNotes: INoteModel[]) => {
        queryClient.setQueryData(['get-notes', NoteTypes.Personal], () => {
            return updatedNotes;
        });
    };

    const handleAddNoteClick = () => {
        const notesCopy = cloneDeep(notes);
        const newNote: INoteModel = {
            status: {
                id: NoteStatusTypes.Open,
            },
            type: {
                id: NoteTypes.Personal,
            },
            purpose: { id: NotePurposeTypesEnum.Info },
        };
        notesCopy.push(newNote);

        updateCachedNotes(notesCopy);
    };

    const handleCloseNoteClick = async (guid?: string) => {
        const notesCopy = cloneDeep(notes);

        if (!guid) {
            updateCachedNotes(notesCopy.filter((note) => note.guid !== undefined));
            return;
        }

        setPendingDeleteGuids([...pendingDeleteGuids, guid ?? '']);

        // Closing/ Completing an existing note.
        const match = notesCopy.find((x) => x.guid === guid);
        if (match) {
            match.status = { id: NoteStatusTypes.Closed };
            const response = await zoneContext.zoneApiClient.saveNote(match);

            if (response.httpResponse.status === 401) {
                window.location.reload();
                return;
            }

            setPendingDeleteGuids(pendingDeleteGuids.filter((pending) => pending !== guid));

            if (response.errorMessage) {
                showErrorToast('Failed to close Personal Task.', { position: 'bottom-left' });
                return;
            }

            const index = notesCopy.findIndex((x) => x.guid === guid);
            if (index >= 0) {
                notesCopy.splice(index, 1);
                updateCachedNotes(notesCopy);
            }
        }
    };

    const handleUserNoteContentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const notesCopy = cloneDeep(notes);
        notesCopy.filter((x) => !x.guid)[0].content = event.target.value;

        updateCachedNotes(notesCopy);
    };

    const handleSaveNoteClick = async () => {
        const notesCopy = cloneDeep(notes);
        const match = notesCopy.find((x) => !x.guid);

        if (match) {
            setPendingResponse(true);

            const response = await zoneContext.zoneApiClient.saveNote(match);
            if (response.httpResponse.status === 401) {
                window.location.reload();
                return;
            }

            setPendingResponse(false);

            if (response.errorMessage) {
                showErrorToast('Failed to save Personal Task.', { position: 'bottom-left' });
                return;
            }

            if (response.payload) {
                match.guid = response.payload.guid;
                match.modifiedDate = response.payload.modifiedDate;
                updateCachedNotes(notesCopy);
            }
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            handleSaveNoteClick();
        } else if (event.key === 'Escape') {
            handleCloseNoteClick();
        }
    };

    useEffect(() => {
        setShowInput(!!notes.find((x) => !x.guid));
    }, [notes]);

    useEffect(() => {
        if (newNoteInputRef.current) {
            newNoteInputRef.current.focus();
        }
    }, [showInput]);

    return (
        <div>
            <div className="padding-left-5">
                <span className="bold uppercase">Tasks</span>
            </div>
            <hr
                style={{
                    marginTop: '5px',
                    marginBottom: '5px',
                    opacity: 'inherit',
                }}
            />

            <table className="taskbar-table full-width">
                <tbody>
                    <tr>
                        <td
                            colSpan={2}
                            className="color-yellow bold uppercase padding-left-5 font-size-xs"
                        >
                            Personal
                        </td>
                        <td className="text-end">
                            {notes.filter((x) => !x.guid).length === 0 && notes.length < 5 && (
                                <button
                                    className="btn btn-no-bg float-end py-0"
                                    onClick={handleAddNoteClick}
                                >
                                    <i className="fal fa-plus color-yellow" />
                                </button>
                            )}
                        </td>
                    </tr>
                    {notes.map((userNote: INoteModel, index: number) => {
                        return (
                            <tr key={index}>
                                <td width="20" className="padding-left-5">
                                    <LoadingButton
                                        icon="fal fa-check color-white"
                                        loading={pendingDeleteGuids.includes(userNote.guid ?? '')}
                                        loadingText=""
                                        buttonCssClass="btn btn-no-bg"
                                        style={{ padding: '0px' }}
                                        onClick={() => handleCloseNoteClick(userNote.guid)}
                                    />
                                </td>
                                <td className="padding-left-5">
                                    {userNote.guid ? (
                                        <span className="font-size-xs">{userNote.content}</span>
                                    ) : (
                                        <input
                                            className="form-control full-width"
                                            disabled={pendingResponse}
                                            ref={newNoteInputRef}
                                            type="text"
                                            value={userNote.content ?? ''}
                                            onChange={handleUserNoteContentChange}
                                            onKeyDown={handleKeyDown}
                                        />
                                    )}
                                </td>
                                <td>
                                    {!userNote.guid &&
                                        userNote.content &&
                                        userNote.content.length > 0 && (
                                            <LoadingButton
                                                icon="fal fa-plus font-green"
                                                loading={pendingResponse}
                                                loadingText=""
                                                buttonCssClass="btn btn-no-bg"
                                                style={{
                                                    padding: '0px',
                                                    paddingLeft: '10px',
                                                }}
                                                onClick={handleSaveNoteClick}
                                            />
                                        )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {notesApi.isFetching && (
                <Stack style={{ gap: 5, marginTop: 8 }}>
                    <PlaceholderRow />
                    <PlaceholderRow />
                </Stack>
            )}
        </div>
    );
}

function PlaceholderRow() {
    const style: React.CSSProperties = {
        borderRadius: 3,
        opacity: 0.25,
        height: 18,
        animationDelay: '0s',
        animationDuration: '2s',
    };

    return (
        <Placeholder as="div" animation="glow" className="d-flex" style={{ gap: 5 }}>
            <Placeholder bg="secondary" style={{ ...style, flexShrink: 0, width: 40 }} />
            <Placeholder bg="secondary" style={{ ...style, flexGrow: 1 }} />
        </Placeholder>
    );
}
